import { notification } from 'antd';
import { LS_TOKEN_NAME } from '@/constants';
import { dispatchWsMessage } from './registry';

// Module-level singleton, mirroring the pattern in
// useUnreadDispatchNotifications.ts: one shared connection for the whole
// app, established the first time any consumer asks for it
// (ensureNotificationSocketConnected() is idempotent).
//
// This file only owns the connection lifecycle. It has no idea CSR prompts
// or notification badges exist — every parsed message is handed to
// dispatchWsMessage() (registry.ts), which routes it to whichever feature
// called registerWsHandler() for that `type`. If WS_URL isn't configured, or
// the socket is down, nothing changes — each feature's own REST polling
// stays the fallback.
// Fixed backoff steps. Once reconnectAttempt runs past the end of this list,
// Math.min() below keeps clamping to the last entry — so retries never stop,
// they just settle at a flat 1min cadence once the escalation steps are exhausted.
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000, 60_000];

export type NotificationSocketStatus = 'disabled' | 'connecting' | 'open' | 'reconnecting';

const STATUS_EVENT = 'app:notification-socket-status';
const RECONNECTED_EVENT = 'app:notification-socket-reconnected';

// Manual-testing toast (see ws.onmessage below) is restricted to this one
// account so it doesn't pop up for every tenant user in production.
// const DEBUG_TOAST_EMAIL = ['patryksypien9@gmail.com', 'csr1@gmail.com'];
const DEBUG_TOAST_EMAIL: string[] = [];

/**
 * Peeks at the (already-trusted, server-issued) JWT's `email` claim without
 * verifying the signature — fine here since this only gates a debug UI
 * element, never anything security-sensitive. Returns null on any decode
 * failure (malformed token, non-JWT, etc).
 */
function decodeJwtEmail(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json)?.email ?? null;
  } catch {
    return null;
  }
}

function isDebugToastUser(): boolean {
  const token = localStorage.getItem(LS_TOKEN_NAME);
  return !!token && DEBUG_TOAST_EMAIL.includes(decodeJwtEmail(token)!);
}

let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let started = false;
let hasConnectedOnce = false;
let status: NotificationSocketStatus = WS_URL ? 'connecting' : 'disabled';
let lastConnectedAt: number | null = null;
let lastDisconnectedAt: number | null = null;
let lastMessageAt: number | null = null;
let lastToastKey: string | null = null;
let lastToastAt = 0;

// A single backend event can legitimately fan out into several near-identical WS
// pushes in a tight burst (e.g. a poll run updating multiple rows, each firing its
// own broadcast of the same tenant-wide snapshot). The debug toast below bypasses
// the per-type coalescing in registry.ts (it fires on the raw frame, before that
// routing), so without this it pops once per message in the burst. Suppress only
// back-to-back *identical* messages — anything with new content still shows.
const TOAST_DEDUPE_MS = 1000;

const hasAuthToken = () => (localStorage.getItem(LS_TOKEN_NAME) || '').length > 20;

function setStatus(next: NotificationSocketStatus) {
  status = next;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: next }));
  }
}

/** Reactive status for UI indicators (bell dot, debug page). */
export function onNotificationSocketStatusChanged(handler: (status: NotificationSocketStatus) => void) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<NotificationSocketStatus>).detail);
  window.addEventListener(STATUS_EVENT, listener);
  return () => window.removeEventListener(STATUS_EVENT, listener);
}

/**
 * Fires when the socket comes back up after having been open before — an
 * actual reconnect, not this page load's first connection. No hop in the
 * publish chain (PHP -> relay -> this socket) retries or persists a dropped
 * push, so whatever changed during the outage never arrives on its own; every
 * feature with a REST fallback should use this to resync immediately instead
 * of waiting out its own poll interval. The first-ever connect is excluded
 * since each feature already gets fresh state from its own mount-time fetch.
 */
export function onSocketReconnected(handler: () => void) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(RECONNECTED_EVENT, handler);
  return () => window.removeEventListener(RECONNECTED_EVENT, handler);
}

/** Point-in-time snapshot for the debug page — not reactive, call on demand. */
export function getNotificationSocketDebugInfo() {
  return {
    status,
    wsUrl: WS_URL || null,
    reconnectAttempt,
    lastConnectedAt,
    lastDisconnectedAt,
    lastMessageAt,
  };
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  const delay = RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)];
  reconnectAttempt++;
  console.log(`[websocket] reconnecting in ${delay}ms (attempt ${reconnectAttempt})`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

// Bypasses any pending backoff timer and retries right away — used when the
// browser tells us the network just came back, instead of waiting out
// whatever delay was in flight (up to 30s). Also exposed for a manual
// "Reconnect now" button on the debug page.
export function reconnectNow() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    socket.close();
    return;
  }
  connect();
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', reconnectNow);
}

function connect() {
  if (!WS_URL || socket) return;

  const token = localStorage.getItem(LS_TOKEN_NAME);
  if (!token || !hasAuthToken()) {
    scheduleReconnect();
    return;
  }

  console.log('[websocket] connecting…', WS_URL);
  setStatus('connecting');
  const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
  socket = ws;

  ws.onopen = () => {
    reconnectAttempt = 0;
    lastConnectedAt = Date.now();
    setStatus('open');
    console.log('[websocket] connected');
    if (hasConnectedOnce && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(RECONNECTED_EVENT));
    }
    hasConnectedOnce = true;
  };

  ws.onmessage = (event) => {
    lastMessageAt = Date.now();
    console.log('[websocket] message received:', event.data);
    try {
      const msg = JSON.parse(event.data);
      if (isDebugToastUser()) {
        const toastKey = `${msg.type}:${msg.data ? JSON.stringify(msg.data) : ''}`;
        const now = Date.now();
        const isDuplicateBurst = toastKey === lastToastKey && now - lastToastAt < TOAST_DEDUPE_MS;
        lastToastKey = toastKey;
        lastToastAt = now;
        if (!isDuplicateBurst) {
          notification.open({
            message: 'WebSocket notification received',
            description: `type: ${msg.type}, data: ${msg.data ? JSON.stringify(msg.data) : ''}`,
            duration: 10,
            closable: true,
          });
        }
      }
      dispatchWsMessage(msg);
    } catch {
      // Ignore malformed frames
    }
  };

  ws.onclose = (event) => {
    console.log('[websocket] closed', event.code, event.reason);
    socket = null;
    lastDisconnectedAt = Date.now();
    setStatus('reconnecting');
    scheduleReconnect();
  };

  ws.onerror = (event) => {
    console.log('[websocket] error', event);
    ws.close();
  };
}

/** Idempotent — safe to call from every consumer's mount effect. */
export function ensureNotificationSocketConnected() {
  if (started) return;
  started = true;
  connect();
}
